import SupportPostSearchBar from 'components/body/SupportPostSearchBar'
import GoogleTagManagerAttributes from 'components/core/GoogleTagManagerAttributes'
import HeroWithCustomComponent from 'components/hero/HeroWithCustomComponent'
import Footer from 'components/layout/Footer'
import AnyHeroWrapper from 'components/renderers/AnyHeroWrapper'
import HeaderContainer from 'components/renderers/HeaderContainer'
import Env from 'Env'
import { TopPosterModel } from 'models/body/TopPosterModel'
import { SupportTopicModel } from 'models/core/SupportTopicModel'
import { MenuModel } from 'models/menu/MenuModel'
import { GetStaticPaths, GetStaticProps } from 'next'
import { NextSeo } from 'next-seo'
import Head from 'next/head'
import Link from 'next/link'
import React, { Fragment, FunctionComponent } from 'react'
import { ApiPromise } from 'services/ApiPromise'
import { Converters } from 'services/Converters'
import { useRouter } from 'next/router'
import ErrorPage from 'next/error'

type PathsParamsType = {
  params: { slug: string }
}

type Props = {
  menu: MenuModel
  footer: MenuModel
  allTopics: SupportTopicModel['data'][]
  topic: SupportTopicModel['data']
  posters: {
    data: TopPosterModel[]
  }
}

const SupportTopicSlug: FunctionComponent<Props> = (props) => {
  const router = useRouter()
  if (!router.isFallback && !props.topic) {
    return <ErrorPage statusCode={404} />
  }
  
  return (
    <Fragment>
      <Head>
        <title>Article Category - {props.topic.attributes.title}</title>
      </Head>
      <NextSeo title={`Article Category - ${props.topic.attributes.title}`} />
      <GoogleTagManagerAttributes />

      <HeaderContainer posters={props.posters} menu={props.menu} />

      <AnyHeroWrapper componentClassNames={`inner-banner md:pb-[65px] md:text-2xl pt-[50px] pb-[60px] bg-inner-banner bg-center bg-no-repeat bg-cover text-center z-10 relative text-lg text-white font-lato`}>
        <HeroWithCustomComponent data={{ title: 'Have a question?' }}>
          <SupportPostSearchBar />
        </HeroWithCustomComponent>
      </AnyHeroWrapper>
      <div className="pt-14 container lg:flex gap-x-8">
        <div className="w-full lg:w-1/3">
          <h2 className="text-2xl font-semibold font-lato">Support Categories</h2>
          <ul className="mt-2">
            {props.allTopics.map((topic, index) => (
              <Link key={index} href={`/article-categories/${topic.attributes.slug}`}>
                <li
                  style={router.query.slug === topic.attributes.slug ? { color: 'rgb(249,100,0)' } : undefined}
                  className={`border-b border-gray-300 text-lg font-lato py-2 text-gray-600 cursor-pointer hover:text-[rgb(249,100,0)]`}
                >
                  {topic.attributes.title}
                </li>
              </Link>
            ))}
          </ul>
        </div>
        <div className="w-full mt-10 lg:mt-0">
          <h2 className="text-gray-800 text-3xl font-bold font-lato">{props.topic.attributes.title}</h2>
          <ul className="mt-4 text-2xl font-normal">
            {props.topic.attributes.posts.data.map((post, index) => (
              <Link key={index} href={`/support/${post.attributes.slug}`}>
                <li className="cursor-pointer hover:text-[rgb(249,100,0)]">{post.attributes.title}</li>
              </Link>
            ))}
          </ul>
        </div>
      </div>
      <Footer data={props.footer} />
    </Fragment>
  )
}

export const getStaticProps: GetStaticProps = async (context) => {
  const convert = (input: any) => Converters.KeysConvert.ToLowerCase(input)
  const slug = context.params?.slug as string
  const isPreview = context.preview
  const menuAndFooter = ApiPromise.Layout.GetMainAndFooterMenu()
  const [menuPromise, footerPromise, postersPromise] = await Promise.all(menuAndFooter)
  const [menu, footer, posters] = Converters.KeysConvert.ConvertAllToLower([menuPromise.data, footerPromise.data, postersPromise.data])
  const allTopics = await ApiPromise.Blog.SupportTopics().then((resp) => convert(resp.data))
  const topic = await ApiPromise.Blog.SupportTopicBySlug(slug, isPreview).then((resp) => convert(resp.data))
  return { props: { allTopics: allTopics.data, topic: topic.data, menu, footer, posters, isPreview: isPreview ? true : false }, revalidate: Env.PropsRevalidateTime }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const convert = (input: any) => Converters.KeysConvert.ToLowerCase(input)
  const topics = await ApiPromise.Blog.SupportTopics().then((resp) => convert(resp.data))
  const pageRecordsData: any[] = topics['data']
  const pathsParamsRecord: PathsParamsType[] = []
  pageRecordsData.map((record) => {
    if (record.attributes && record.attributes.slug) {
      pathsParamsRecord.push({ params: { slug: record.attributes.slug } })
    }
  })
  return { paths: pathsParamsRecord, fallback: false }
}

export default SupportTopicSlug
